import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import JoinModal from '../../components/app/JoinModal';
import { Group, JoinRequest } from '../../types/groups';
import { CATEGORY_EMOJI, CATEGORY_GRADIENT } from '../../constants/groups';
import { apiGet, apiPost } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { useUserLocation, calcDistance, formatDistance } from '../../hooks/useUserLocation';
import { slugify, extractId } from '../../utils/slug';

export default function AppGroupDetail() {
  const { id: slugParam } = useParams<{ id: string }>();
  const id = slugParam ? extractId(slugParam) : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const community = (location.state as { community?: string } | null)?.community || searchParams.get('community') || undefined;
  const { authUser, loading: authLoading } = useAuth();
  const userCoords = useUserLocation();

  const [group, setGroup] = useState<Group | null>(null);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  const fetchGroup = async (communityHint?: string) => {
    if (!id) return;
    setLoadingGroup(true);
    setError(null);
    try {
      const params: Record<string, string> = { id: id! };
      const comm = communityHint || community || group?.community;
      if (comm) params.community = comm;
      if (authUser) {
        const res = await apiGet<{ data: Group }>('/api_group_details', params, true);
        setGroup(res.data);
      } else {
        const res = await apiGet<{ data: Group }>('/api_group_details-public', params);
        setGroup(res.data);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load group');
    } finally {
      setLoadingGroup(false);
    }
  };

  useEffect(() => {
    if (!authLoading) fetchGroup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authUser, authLoading]);

  useEffect(() => {
    if (!group || !authUser || authUser.userId !== group.creator_id) return;
    setLoadingRequests(true);
    apiGet<{ data: JoinRequest[] }>('/api_group_join_requests', { group_id: group.id }, true)
      .then((res) => setJoinRequests((res.data || []).filter((r) => r.status === 'pending')))
      .catch(console.error)
      .finally(() => setLoadingRequests(false));
  }, [group, authUser]);

  const handleApproveReject = async (requestId: string, action: 'approve' | 'reject') => {
    setProcessingRequest(requestId + action);
    try {
      await apiPost('/api_approve_join_request', { request_id: requestId, action }, true);
      setJoinRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err: unknown) {
      console.error(err);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleShare = () => {
    const link = group
      ? `https://sahayy.com/groups/${slugify(group.name)}-${group.id}?community=${encodeURIComponent(group.community || '')}`
      : window.location.href;
    const emoji = group ? (CATEGORY_EMOJI[group.category] || '💬') : '';
    const message = group
      ? `👥 ${group.name}\n${emoji} ${group.category}${group.location ? `\n📍 ${group.location}` : ''}${group.community ? `\n🏘️ ${group.community}` : ''}\n\nJoin on Sahayy 👇\n${link}`
      : link;
    navigator.clipboard.writeText(message).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleJoinSuccess = () => {
    setShowJoinModal(false);
    fetchGroup();
  };

  /* ── Loading ── */
  if (loadingGroup || authLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
        <AppNav />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error || !group) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
        <AppNav />
        <div style={{ maxWidth: '480px', margin: '6rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
          <p style={{ fontSize: '2.5rem', margin: '0 0 1rem' }}>🔍</p>
          <h2 style={{ color: '#111', fontWeight: 700, margin: '0 0 0.5rem', fontSize: '1.25rem' }}>Group not found</h2>
          <p style={{ color: '#6b7280', margin: '0 0 2rem', fontSize: '0.95rem' }}>{error || 'This group does not exist.'}</p>
          <button onClick={() => navigate('/groups')} style={{ padding: '0.75rem 2rem', backgroundColor: '#111', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
            ← Back to Groups
          </button>
        </div>
      </div>
    );
  }

  const isCreator = authUser?.userId === group.creator_id;
  const categoryEmoji = CATEGORY_EMOJI[group.category] || '💬';
  const gradient = CATEGORY_GRADIENT[group.category] || CATEGORY_GRADIENT['Other'];
  const images = group.images?.filter(Boolean) || [];
  const hasImage = images.length > 0;
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const deepLink = `sahayy://groups/${group.id}?community=${encodeURIComponent(group.community || '')}`;

  const JoinButton = ({ full = false }: { full?: boolean }) => {
    const base: React.CSSProperties = {
      padding: '0.875rem 1.75rem',
      border: 'none',
      borderRadius: '10px',
      fontWeight: 600,
      fontSize: '0.95rem',
      cursor: 'pointer',
      transition: 'opacity 0.15s',
      width: full ? '100%' : undefined,
    };
    if (!authUser) return (
      <button onClick={() => navigate('/login')} style={{ ...base, background: 'linear-gradient(135deg,#FF385C,#e11d48)', color: '#fff', boxShadow: '0 4px 14px rgba(255,56,92,0.35)' }}>
        Sign in to Join
      </button>
    );
    if (group.is_member) return (
      <button disabled style={{ ...base, backgroundColor: '#f0fdf4', color: '#16a34a', border: '1.5px solid #bbf7d0', cursor: 'default' }}>
        ✓ You're a member
      </button>
    );
    if (group.status === 'suspended') return null;
    return (
      <button onClick={() => setShowJoinModal(true)} style={{ ...base, background: 'linear-gradient(135deg,#FF385C,#e11d48)', color: '#fff', boxShadow: '0 4px 14px rgba(255,56,92,0.35)' }}>
        Request to Join
      </button>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />

      {/* ── Content (hero lives inside the content container) ── */}
      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '1.25rem 1.5rem 8rem' }}>

        {/* Back + Share row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
          <button
            onClick={() => navigate('/groups')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#111', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}
          >
            ← All Groups
          </button>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {isCreator && (
              <button
                onClick={() => navigate(`/groups/edit?id=${group.id}&community=${encodeURIComponent(group.community || '')}`)}
                style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#111', transition: 'all 0.2s' }}
              >
                ✏️ Edit
              </button>
            )}
            <button
              onClick={handleShare}
              style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: copied ? '#16a34a' : '#111', transition: 'all 0.2s' }}
            >
              {copied ? '✓ Copied' : '↑ Share'}
            </button>
          </div>
        </div>

        {/* ── Hero: mobile = carousel, desktop = tiled grid ── */}

        {/* Mobile carousel */}
        <div className="hero-carousel" style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
          {hasImage ? (
            <img key={imgIndex} src={images[imgIndex]} alt={group.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '5rem' }}>{categoryEmoji}</span>
            </div>
          )}
          {images.length > 1 && (
            <>
              <button onClick={() => setImgIndex(i => (i - 1 + images.length) % images.length)} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>‹</button>
              <button onClick={() => setImgIndex(i => (i + 1) % images.length)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>›</button>
              <div style={{ position: 'absolute', bottom: '10px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '5px' }}>
                {images.map((_, i) => <div key={i} onClick={() => setImgIndex(i)} style={{ width: i === imgIndex ? '16px' : '6px', height: '6px', borderRadius: '3px', backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.2s' }} />)}
              </div>
            </>
          )}
          {group.status === 'suspended' && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(220,38,38,0.88)', color: '#fff', textAlign: 'center', padding: '0.5rem', fontSize: '0.82rem', fontWeight: 600 }}>
              ⚠️ This group is suspended and not accepting new members
            </div>
          )}
        </div>

        {/* Desktop tiled grid */}
        {/* Always: 1 large left + 2×2 right. Missing slots → gray placeholder */}
        <div className="hero-grid" style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '420px', borderRadius: '16px', overflow: 'hidden' }}>

          {/* Main large image */}
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {images[0] ? (
              <img src={images[0]} alt={group.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '6rem', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}>{categoryEmoji}</span>
              </div>
            )}
            {group.status === 'suspended' && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(220,38,38,0.88)', color: '#fff', textAlign: 'center', padding: '0.5rem', fontSize: '0.82rem', fontWeight: 600 }}>
                ⚠️ Suspended
              </div>
            )}
          </div>

          {/* Right 2×2 grid — always 4 slots */}
          <div style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
            {[1, 2, 3, 4].map((idx) => {
              const src = images[idx];
              const isLast = idx === 4;
              const hiddenCount = images.length - 5;
              return (
                <div key={idx} style={{ position: 'relative', overflow: 'hidden' }}>
                  {src ? (
                    <img src={src} alt={`${group.name} ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #f0f0ee 0%, #e8e6e3 100%)' }} />
                  )}
                  {isLast && hiddenCount > 0 && (
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>+{hiddenCount} more</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="detail-columns" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '4rem', alignItems: 'start' }}>

          {/* ── Left column ── */}
          <div>
            {/* Title & meta */}
            <div style={{ padding: '2rem 0 1.5rem', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <h1 style={{ color: '#111', fontWeight: 800, fontSize: 'clamp(1.5rem,3vw,2rem)', margin: 0, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                  {group.name}
                </h1>
                {group.is_private && (
                  <span style={{ backgroundColor: '#fef9c3', color: '#854d0e', fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: '20px', border: '1px solid #fef08a', whiteSpace: 'nowrap' }}>
                    🔒 Private
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', color: '#6b7280', fontSize: '0.9rem' }}>
                <span style={{ fontWeight: 600, color: '#111' }}>
                  {categoryEmoji} {group.category}
                </span>
                {group.city && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(group.city)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'inherit', textDecoration: 'none' }}>
                    📍 {group.city}
                    {userCoords && group.latitude && group.longitude && (
                      <span style={{ color: '#10b981', marginLeft: '6px', fontSize: '0.82rem', fontWeight: 600 }}>{formatDistance(calcDistance(userCoords.lat, userCoords.lng, group.latitude, group.longitude))}</span>
                    )}
                    {' '}<span style={{ color: '#FF385C', fontSize: '0.75rem', fontWeight: 600 }}>↗</span>
                  </a>
                )}
                {group.community && <span style={{ color: '#9ca3af' }}>{group.community}</span>}
              </div>
            </div>

            {/* External link */}
            {group.external_group_link && (
              <div style={{ padding: '1.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
                <h2 style={{ margin: '0 0 0.875rem', fontWeight: 700, fontSize: '1rem', color: '#111', letterSpacing: '-0.01em' }}>Group Link</h2>
                <a href={group.external_group_link} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#FF385C', fontSize: '0.9rem', fontWeight: 600, textDecoration: 'none', wordBreak: 'break-all' }}>
                  🔗 {group.external_group_link} <span style={{ fontSize: '0.75rem' }}>↗</span>
                </a>
              </div>
            )}

            {/* Description */}
            <div style={{ padding: '1.5rem 0', borderBottom: isCreator ? '1px solid #f0f0f0' : undefined }}>
              <h2 style={{ margin: '0 0 0.875rem', fontWeight: 700, fontSize: '1rem', color: '#111', letterSpacing: '-0.01em' }}>About this group</h2>
              <p style={{ color: '#374151', fontSize: '0.975rem', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>
                {group.description}
              </p>
              {group.tags && group.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1.25rem' }}>
                  {group.tags.map(tag => (
                    <span key={tag} style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151', fontSize: '0.8rem', padding: '4px 12px', borderRadius: '20px', fontWeight: 500 }}>
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Join Requests (creator only) */}
            {isCreator && (
              <div style={{ padding: '1.5rem 0' }}>
                <h2 style={{ margin: '0 0 1.25rem', fontWeight: 700, fontSize: '1rem', color: '#111', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Join Requests
                  {joinRequests.length > 0 && (
                    <span style={{ backgroundColor: '#FF385C', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px' }}>
                      {joinRequests.length}
                    </span>
                  )}
                </h2>

                {loadingRequests ? (
                  <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Loading...</p>
                ) : joinRequests.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: '0.9rem', margin: 0 }}>No pending requests.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {joinRequests.map((req) => {
                      const initials = (req.user_profile?.name || req.user_id).slice(0, 2).toUpperCase();
                      return (
                        <div key={req.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem', padding: '1rem 1.125rem', backgroundColor: '#fafafa', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                          {/* Avatar */}
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FF385C15', color: '#FF385C', fontWeight: 700, fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {initials}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: '0 0 0.1rem', fontWeight: 600, fontSize: '0.9rem', color: '#111' }}>{req.user_profile?.name || req.user_id}</p>
                            {req.user_profile && (
                              <p style={{ margin: '0 0 0.35rem', fontSize: '0.78rem', color: '#9ca3af' }}>
                                {[req.user_profile.nationality, req.user_profile.city].filter(Boolean).join(' · ')}
                              </p>
                            )}
                            {req.message && (
                              <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280', fontStyle: 'italic' }}>"{req.message}"</p>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                            <button
                              onClick={() => handleApproveReject(req.id, 'reject')}
                              disabled={processingRequest !== null}
                              style={{ padding: '0.4rem 0.875rem', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#374151', fontSize: '0.82rem', fontWeight: 500, cursor: processingRequest !== null ? 'not-allowed' : 'pointer', opacity: processingRequest !== null ? 0.5 : 1 }}
                            >
                              {processingRequest === req.id + 'reject' ? '…' : 'Decline'}
                            </button>
                            <button
                              onClick={() => handleApproveReject(req.id, 'approve')}
                              disabled={processingRequest !== null}
                              style={{ padding: '0.4rem 0.875rem', backgroundColor: '#111', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: processingRequest !== null ? 'not-allowed' : 'pointer', opacity: processingRequest !== null ? 0.5 : 1 }}
                            >
                              {processingRequest === req.id + 'approve' ? '…' : 'Approve'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Right sticky column ── */}
          <div className="detail-sidebar" style={{ position: 'sticky', top: '80px', paddingTop: '2rem' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 6px 30px rgba(0,0,0,0.08)' }}>
              {/* Member count */}
              <p style={{ margin: '0 0 1.25rem', fontWeight: 700, fontSize: '0.95rem', color: '#374151' }}>
                👥 <strong style={{ color: '#111' }}>{group.member_count.toLocaleString()}</strong> members
              </p>

              <div style={{ marginBottom: '1rem' }}>
                <JoinButton full />
              </div>

              <button
                onClick={handleShare}
                style={{ width: '100%', padding: '0.75rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px', color: '#111', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = '#111')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
              >
                {copied ? '✓ Message copied!' : '↑ Share group'}
              </button>

              {/* App promo */}
              <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: '#374151', fontWeight: 600 }}>Get the full experience</p>
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: '#9ca3af' }}>Chat, connect and more in the app</p>
                <a
                  href={isMobileDevice ? deepLink : 'https://sahayy.com/download'}
                  target={isMobileDevice ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', backgroundColor: '#111', color: '#fff', padding: '0.5rem 1.25rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}
                >
                  Open in App →
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Mobile sticky bottom bar ── */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTop: '1px solid #f0f0f0', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', zIndex: 50 }}
        className="mobile-bottom-bar"
      >
        <div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#111' }}>{group.member_count.toLocaleString()} {group.member_count === 1 ? 'member' : 'members'}</p>
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#9ca3af' }}>in this group</p>
        </div>
        <JoinButton />
      </div>

      <style>{`
        @media (min-width: 768px) {
          .mobile-bottom-bar { display: none !important; }
          .hero-carousel { display: none !important; }
        }
        @media (max-width: 767px) {
          .hero-grid { display: none !important; }
          .detail-columns { display: block !important; }
          .detail-sidebar { display: none !important; }
        }
      `}</style>

      {showJoinModal && group && (
        <JoinModal
          groupId={group.id}
          groupName={group.name}
          community={group.community}
          onClose={() => setShowJoinModal(false)}
          onSuccess={handleJoinSuccess}
        />
      )}
    </div>
  );
}
